<template>
  <StaffLayout section="Respond Libraries">
    <div class="pb-4">
      <BackButton to="../respondlibraries"> Back to Response Libraries List </BackButton>
      <div class="pt-4 lg:pt-0 lg:float-right">
        <!-- Use a form to handle "enter" keystroke -->
        <form v-on:submit.prevent>
          <input
            v-model.lazy="responseSearchString"
            placeholder="Search this library"
            class="shadow appearance-none border rounded py-1 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
          <SearchButton @click="searchLibrary" type="submit"> Search </SearchButton>
        </form>
      </div>
    </div>
    <div v-if="isLoading"><Loading /></div>
    <div v-else-if="library">
      <h1 class="py-3 mb-6 border-b flex">
        <div class="text-xl flex-grow">
          <div class="font-bold">Edit Response Library</div>
          <div>
            {{ library.name }}
          </div>
        </div>
        <EditButton @click="editLibraryClick" :disabled="editMode !== EditMode.None">
          Edit
        </EditButton>
      </h1>
      <div>
        <div class="border-b pb-6">
          <h2 class="font-bold">Role</h2>
          <div class="lg:w-1/2 2xl:w-1/3">
            <BaseSelect
              :options="roleOptionsFiltered"
              v-model="selectedRoleType"
              :default-option="({ name: '--Please Select--', value: -1 } as NameValue)"
              :disabled="editMode !== EditMode.None"
            />
          </div>
        </div>
        <div class="my-5 mx-3">
          <AddButton
            v-if="editMode !== EditMode.NewResponse"
            class="mb-2"
            @click="addResponse"
            :disabled="editMode !== EditMode.None"
          >
            Add New Scenario
          </AddButton>
          <div v-else class="pb-3">
            <h1 class="text-lg font-bold">New Scenario</h1>
            <Loading v-if="isSaving" />
            <ErrorDisplay :is-visible="saveError.length > 0" :message="saveError" />
            <div class="lg:w-1/2 2xl:w-1/3">
              <BaseSelect
                label="Role"
                :options="allRoleOptions"
                v-model="editRoleType"
                :error="errors.editRoleType"
                :default-option="({ name: '--Please Select--', value: -1 } as NameValue)"
              />
            </div>
            <div class="lg:w-1/2 2xl:w-1/3">
              <BaseSelect
                label="Response Scenario"
                :options="responseTypeOptions"
                v-model="editResponseType"
                :error="errors.editResponseType"
                :default-option="({ name: '--Please Select--', value: -1 } as NameValue)"
              />
            </div>
            <div class="ckeditor-text mb-3 pt-4">
              <component v-if="Ckeditor && Editor" :is="Ckeditor" :editor="Editor" v-model="editResponseText" />
            </div>
            <div class="mt-3 flex">
              <div class="flex-grow" />
              <BaseButton @click="saveNewResponse" class="mr-4"> Save </BaseButton>
              <BaseButton @click="editMode = EditMode.None">Cancel</BaseButton>
            </div>
          </div>
          <div v-if="selectedRoleItems && selectedRoleItems.length > 0">
            <Disclosure v-for="(response, i) in selectedRoleItems" :key="i">
              <DisclosureButton
                class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-gray-900 border-t hover:bg-blue-200 cursor-pointer focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75"
                :class="response.responseText.length > 0 ? 'bg-gray-100' : 'bg-gray-300'"
                @click="selectClick(response)"
              >
                <span>{{ response.responseTypeName }}</span>
                <span v-if="response.responseText.length === 0">(empty)</span>
              </DisclosureButton>
              <div v-if="editLibItemId == response.responseLibItemId">
                <DisclosurePanel static>
                  <div class="m-3">
                    <div v-if="editMode === EditMode.EditResponse" class="pb-3">
                      <Loading v-if="isSaving" />
                      <ErrorDisplay
                        :is-visible="saveError.length > 0"
                        :message="saveError"
                      />
                      <div class="ckeditor-text mb-3">
                        <component v-if="Ckeditor && Editor" :is="Ckeditor" :editor="Editor" v-model="editResponseText" />
                      </div>
                      <div class="mt-3 flex">
                        <BaseButton
                          @click="deleteResponse(response)"
                          class="text-blue-900 bg-red-100"
                        >
                          Delete
                        </BaseButton>
                        <div class="flex-grow" />
                        <BaseButton @click="saveResponse" class="mr-4"> Save </BaseButton>
                        <BaseButton @click="editMode = EditMode.None">Cancel</BaseButton>
                      </div>
                    </div>
                    <div v-else>
                      <EditButton @click="editResponse(response)" class="h-10">
                        Edit Response
                      </EditButton>
                      <div class="border my-2 p-2 response-steps text-gray-500">
                        <span v-html="response.responseText"></span>
                      </div>
                      <EditButton @click="editResponse(response)" class="h-10">
                        Edit Response
                      </EditButton>
                    </div>
                  </div>
                </DisclosurePanel>
              </div>
            </Disclosure>
          </div>
        </div>
      </div>
    </div>
    <div v-else>No Library found</div>

    <!-- Edit library popup -->
    <EditPopup
      entity-name="Respond Library"
      :is-visible="isEditLibraryPopupVisible"
      :is-edit-mode="true"
      :save-error="saveError"
      :delete-enabled="true"
      @close="isEditLibraryPopupVisible = false"
      @save="saveLibraryClick"
      @delete="deleteLibrary"
    >
      <BaseInput
        label="Library Name"
        v-model="libraryName"
        :error="libraryErrors.libraryName"
      />
    </EditPopup>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useRespondStore } from "../../store/respondStore";
import { useAdminStore } from "../../store/adminStore";

import {
  ResponseLibItemDTO,
  SaveResponseLib,
  SaveResponseLibItem,
} from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

import router from "../../router";
import { NameValue } from "../../sharedTypes";
import { useCKEditor } from "../../composables/useCKEditor";

// Load CKEditor using composable
const { Ckeditor, Editor } = useCKEditor();

const respondStore = useRespondStore();
const adminStore = useAdminStore();
const route = useRoute();

onMounted(async () => {
  isLoading.value = true;
  let responseLibId = Number.parseInt(route.params.libId.toString());
  await respondStore.getLibrary(responseLibId);
  await respondStore.getResponseTypes();
  await adminStore.getRoleTypes();
  isLoading.value = false;
});

const library = computed(() => respondStore.selectedLibrary);

const responseTypeOptions = computed(() =>
  respondStore.responseTypes.map((t) => {
    return {
      name: t.name,
      value: t.responseTypeId,
    } as NameValue;
  })
);

let allRoleOptions = computed(() =>
  adminStore.roleTypes.map(
    (r) =>
      ({
        name: r.name + (r.isStaffRole ? " (Staff)" : ""),
        value: r.roleTypeId,
      } as NameValue)
  )
);

const roleOptionsFiltered = computed(() => {
  if (library.value?.responseLibItems) {
    let options = library?.value?.responseLibItems.map((i) => {
      return {
        name: i.roleName + (getRoleTypeByName(i.roleName)?.isStaffRole ? " (Staff)" : ""),
        value: i.roleTypeId,
      } as NameValue;
    });

    const values = options?.map((o) => o.value);
    const filtered = options?.filter(
      ({ value }, index) => !values?.includes(value, index + 1)
    );

    return filtered?.sort((a, b) => {
      if (getRoleTypeById(a.value).isPinned == getRoleTypeById(b.value).isPinned)
        return a.name.localeCompare(b.name);
      return getRoleTypeById(a.value).isPinned ? -1 : 1;
    });
  }
  return [];
});

const selectedRoleItems = computed(() => {
  var lib = library.value;
  if (lib?.responseLibItems) {
    var items = lib?.responseLibItems.filter(
      (i) => i.roleTypeId?.toString() === selectedRoleType.value
    );
    return items as ResponseLibItemDTO[];
  }
  return [] as ResponseLibItemDTO[];
});

let selectedRoleType = ref<string | number>(-1);

let isLoading = ref(false);
let saveError = ref("");

//-----------------------------------
// #region Inline scenario edit
//-----------------------------------

enum EditMode {
  None,
  EditResponse,
  NewResponse,
}

let isSaving = ref(false);
let editMode = ref(EditMode.None);

let editLibItemId = ref(0);
const { value: editRoleType } = useField<string | number>("editRoleType");
const { value: editResponseType } = useField<string | number>("editResponseType");
let editResponseText = ref("");

function selectClick(response: ResponseLibItemDTO) {
  // Prevent selection when editing
  if (editMode.value !== EditMode.None) return;

  // Same item clicked twice => Toggle selection
  if (response.responseLibItemId === editLibItemId.value) {
    editLibItemId.value = 0;
  } else {
    editLibItemId.value = response.responseLibItemId;
  }
}

function editResponse(response: ResponseLibItemDTO) {
  editRoleType.value = selectedRoleType.value;
  editResponseType.value = response.responseTypeId;
  editResponseText.value = response.responseText;

  saveError.value = "";
  editMode.value = EditMode.EditResponse;
}

async function saveResponse() {
  if (isSaving.value) return;
  isSaving.value = true;
  saveError.value = "";

  let saveResponseDTO = {
    responseLibItemId: editLibItemId.value ?? 0,
    responseLibId: library.value?.responseLibId,
    responseTypeId: editResponseType.value,
    roleTypeId: editRoleType.value,
    responseText: editResponseText.value,
  } as SaveResponseLibItem;

  saveError.value = await respondStore.SaveResponseLibItem(saveResponseDTO);
  if (!saveError.value) {
    isLoading.value = true;
    if (library.value?.responseLibId)
      await respondStore.getLibrary(library.value?.responseLibId, true);

    selectedRoleType.value = editRoleType.value;

    editMode.value = EditMode.None;
    isLoading.value = false;
  }
  isSaving.value = false;
}

async function deleteResponse(response: ResponseLibItemDTO) {
  if (isSaving.value) return;
  isSaving.value = true;
  saveError.value = "";

  saveError.value = await respondStore.deleteResponseLibItem(response.responseLibItemId);
  if (!saveError.value && library.value) {
    await respondStore.getLibrary(library.value.responseLibId, true);
    editMode.value = EditMode.None;

    // select default role if roletype has no more scenarios
    if (!roleOptionsFiltered.value.find((o) => o.value == selectedRoleType.value))
      selectedRoleType.value = -1;
  }
  isSaving.value = false;
}

//-----------------------------------
// #endregion Inline scenario edit
//-----------------------------------

//-----------------------------------
// #region Add response
//-----------------------------------

function addResponse() {
  editLibItemId.value = 0;
  editRoleType.value = selectedRoleType.value;
  editResponseType.value = -1;
  editResponseText.value = "";

  saveError.value = "";
  editMode.value = EditMode.NewResponse;
}

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    editRoleType: yup
      .string()
      // .test("is-selected", "Please select a scenario.", (value, ctx) => {
      //   console.log(parseInt(value));
      //   return parseInt(value) > 0;
      // })
      .label("Role"),
    editResponseType: yup.string().label("Scenario"),
  }),
  initialValues: {
    editRoleType: -1,
    editResponseType: -1,
  },
});

let saveNewResponse = handleSubmit(saveResponse, onInvalidSubmit);
var onInvalidSubmit = () => (saveError.value = "One or more invalid form inputs.");

//-----------------------------------
// #endregion Add response
//-----------------------------------

//-----------------------------------
// #region Edit Library Popup
//-----------------------------------

const { handleSubmit: handleLibrarySubmit, errors: libraryErrors } = useForm({
  validationSchema: yup.object({
    libraryName: yup.string().required().max(50).label("Name"),
  }),
  initialValues: {
    libraryName: "",
  },
});

let isEditLibraryPopupVisible = ref(false);
const { value: libraryName } = useField<string | number>("libraryName");

function editLibraryClick() {
  libraryName.value = library.value.name;

  saveError.value = "";
  isEditLibraryPopupVisible.value = true;
}

let saveLibraryClick = handleLibrarySubmit(onValidLibrarySubmit, onInvalidSubmit);

async function onValidLibrarySubmit() {
  let saveLibraryDTO = {
    responseLibId: library.value?.responseLibId,
    name: libraryName.value,
  } as SaveResponseLib;

  saveError.value = await respondStore.SaveResponseLib(saveLibraryDTO);
  if (saveError.value) return;

  isEditLibraryPopupVisible.value = false;
  isLoading.value = true;
  await respondStore.getLibrary(library.value?.responseLibId, true);
  respondStore.libraryList = null; // Force reload next time respondlib list is retrieved
  isLoading.value = false;
}

async function deleteLibrary() {
  saveError.value = "";
  saveError.value = await respondStore.deleteResponseLibrary(
    library.value?.responseLibId
  );
  if (!saveError.value && library.value) {
    await respondStore.getLibrary(library.value.responseLibId, true);
    respondStore.libraryList = null; // Force reload next time responselib list is retrieved
    router.push(`../respondlibraries`);
  }
}

//-----------------------------------
// #endregion Edit Library Popup
//-----------------------------------

//-----------------------------------
// #region Search Response Library
//-----------------------------------

let responseSearchString = ref("");
function searchLibrary() {
  respondStore.searchResponseContentByLibrary(
    responseSearchString.value,
    library.value?.responseLibId
  );
  router.push("/staff/respondsearchresults");
}

//-----------------------------------
// #endregion Search Respond Library
//-----------------------------------

// #region Helpers

var getRoleTypeByName = (roleName: string) =>
  adminStore?.roleTypes?.find((r) => r.name === roleName);

var getRoleTypeById = (roleId: number | string) =>
  adminStore?.roleTypes?.find((r) => r.roleTypeId === roleId);

// #endregion Helpers
</script>

<style scoped>
:deep(.ck-editor__editable_inline) {
  min-height: 25vh;
  max-height: max-content;
}

.response-steps,
.ckeditor-text {
  font-size: medium;
}

.response-steps :deep(a),
.ckeditor-text :deep(a) {
  color: blue;
  text-decoration: underline;
  cursor: pointer;
}

.response-steps :deep(a:hover),
.ckeditor-text :deep(a:hover) {
  color: cornflowerblue;
}

.response-steps :deep(ul),
.ckeditor-text :deep(ul) {
  padding-left: 24px;
}

.response-steps :deep(ul > li),
.ckeditor-text :deep(ul > li) {
  font-weight: normal;
  list-style-type: circle;
}

.response-steps :deep(ol),
.ckeditor-text :deep(ol) {
  counter-reset: li;
  margin-left: 0;
  padding-left: 0;
}

.response-steps :deep(ol > li),
.ckeditor-text :deep(ol > li) {
  position: relative;
  margin: 0 0 2px 2em;
  padding: 0 8px;
  list-style: none;
}

.response-steps :deep(ol > li:before),
.ckeditor-text :deep(ol > li:before) {
  content: counter(li);
  counter-increment: li;
  position: absolute;
  top: 0px;
  left: -1em;
  box-sizing: border-box;
}

.response-steps :deep(h2),
.ckeditor-text :deep(h2) {
  font-size: x-large;
}

.response-steps :deep(h3),
.ckeditor-text :deep(h3) {
  font-size: larger;
}

.response-steps :deep(h4),
.ckeditor-text :deep(h4) {
  font-size: large;
}
</style>
